import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCoachClients, usePendingInvites, useResendInvite, useCancelInvite } from "@/lib/coachQueries";
import { useCoachTeams } from "@/lib/teamRecordQueries";

const INDIVIDUAL_FILTERS = [
  ["all", "All"], ["active", "Active"], ["paused", "Paused"], ["completed", "Completed"],
  ["past", "Past"], ["needs_attention", "Needs attention"],
];

function formatSentDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { timeZone: "America/Sao_Paulo", month: "short", day: "numeric" });
}

/**
 * Master v5 Prompt 30 follow-up — pending, not-yet-accepted client
 * invites sit above the real client roster with an "Invite sent"
 * badge and Resend/Cancel actions, so a coach never loses track of
 * who they've invited but who hasn't set a password yet.
 */
function PendingInvites() {
  const { data, isPending } = usePendingInvites();
  const resend = useResendInvite();
  const cancel = useCancelInvite();
  const items = data?.items || [];

  if (isPending || items.length === 0) return null;

  return (
    <div className="mb-6" data-testid="coach-pending-invites">
      <h2 className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-2">Pending invites</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-4xl">
        {items.map((inv) => (
          <div key={inv.invite_id} className="border border-dashed border-ink/20 bg-ink/[0.02] rounded-lg p-4" data-testid={`coach-pending-invite-${inv.invite_id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-sm font-medium text-ink">{inv.client_name}</p>
                <p className="text-xs text-ink-soft">{inv.title}</p>
              </div>
              <span className="text-[10px] uppercase text-terracotta border border-terracotta/30 rounded-full px-2 py-0.5 shrink-0" data-testid={`coach-pending-invite-badge-${inv.invite_id}`}>
                Invite sent
              </span>
            </div>
            <p className="text-xs text-ink-soft mb-3">Sent {formatSentDate(inv.invited_at)} · expires {formatSentDate(inv.expires_at)}</p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={resend.isPending}
                onClick={() => resend.mutate(inv.invite_id, {
                  onSuccess: () => toast.success(`Invite resent to ${inv.email}`),
                  onError: () => toast.error("Couldn't resend the invite."),
                })}
                data-testid={`coach-pending-invite-resend-${inv.invite_id}`}
              >
                Resend
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-700 hover:text-red-800"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(inv.invite_id, {
                  onSuccess: () => toast.success("Invite cancelled"),
                  onError: () => toast.error("Couldn't cancel the invite."),
                })}
                data-testid={`coach-pending-invite-cancel-${inv.invite_id}`}
              >
                Cancel
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Clients Restructure — Individuals + Teams. "Individuals" keeps the
 * existing per-member client roster (access_type='individual' only,
 * filtered further client-side to keep the coach_clients_route
 * endpoint completely unchanged); "Teams" is a NEW one-card-per-Team-
 * Workspace view (never one row per member) whose "View Full Team
 * Record" opens Team 360.
 */
export default function CoachClientsPage() {
  const [section, setSection] = useState("individuals");
  const [filter, setFilter] = useState("all");
  const { data, isPending, isError } = useCoachClients(filter);
  const navigate = useNavigate();
  const items = (data?.items || []).filter((c) => c.access_type === "individual");

  const teamsQuery = useCoachTeams();
  const teams = teamsQuery.data?.items || [];

  return (
    <div data-testid="coach-clients-page">
      <h1 className="font-serif text-2xl text-ink mb-1">Clients</h1>
      <p className="text-sm text-ink-soft mb-4">Every client and team across the Workspaces you coach.</p>

      <PendingInvites />

      <div className="flex items-center gap-2 mb-6" data-testid="coach-clients-section-tabs">
        {[["individuals", "Individuals"], ["teams", "Teams"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setSection(key)}
            className={`text-sm font-medium px-4 py-1.5 rounded-full transition-colors ${section === key ? "bg-ink text-paper" : "text-ink-soft hover:bg-ink/5 border border-ink/10"}`}
            data-testid={`coach-clients-section-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {section === "individuals" ? (
        <>
          <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="coach-clients-filters">
            {INDIVIDUAL_FILTERS.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`text-sm px-3 py-1.5 rounded-full transition-colors ${filter === key ? "bg-ink text-paper" : "text-ink-soft hover:bg-ink/5 border border-ink/10"}`}
                data-testid={`coach-clients-filter-${key}`}
              >
                {label}
              </button>
            ))}
          </div>

          {isPending ? (
            <p className="text-sm text-ink-soft" data-testid="coach-clients-loading">Loading…</p>
          ) : isError ? (
            <p className="text-sm text-red-600" data-testid="coach-clients-error">Couldn't load your clients.</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-ink-soft" data-testid="coach-clients-empty">No clients match this filter.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-4xl" data-testid="coach-clients-list">
              {items.map((c) => (
                <div key={c.member_id} className="border border-ink/15 bg-white/50 rounded-lg p-4" data-testid={`coach-client-card-${c.member_id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-sm font-medium text-ink">{c.member_name}</p>
                      <p className="text-xs text-ink-soft">{c.workspace_title} · {c.access_type}</p>
                    </div>
                    <span className="text-[10px] uppercase text-ink-soft border border-ink/10 rounded-full px-2 py-0.5 shrink-0">{c.status_bucket}</span>
                  </div>
                  <p className="text-xs text-ink-soft mb-1" data-testid={`coach-client-next-session-${c.member_id}`}>
                    {c.next_session_at ? `Next: ${new Date(c.next_session_at).toLocaleDateString("en-US", { timeZone: "America/Sao_Paulo", month: "short", day: "numeric" })}` : "No next session"}
                  </p>
                  {c.open_task_count > 0 && (
                    <p className="text-xs text-terracotta font-medium mb-2" data-testid={`coach-client-open-tasks-${c.member_id}`}>{c.open_task_count} open task{c.open_task_count > 1 ? "s" : ""}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    {c.status_bucket === "active" && (
                      <button
                        onClick={() => navigate(`/rehearsal-room/coach/w/${c.workspace_id}/client/${c.member_id}/prep`)}
                        className="text-sm font-medium bg-terracotta text-paper px-3 py-1.5 rounded-full hover:bg-terracotta/90 transition-colors"
                        data-testid={`coach-client-prepare-next-session-${c.member_id}`}
                      >
                        Prepare Next Session
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/rehearsal-room/coach/w/${c.workspace_id}/client/${c.member_id}/overview`)}
                      className="text-sm font-medium border border-ink/15 px-3 py-1.5 rounded-full text-ink hover:bg-ink/5 transition-colors"
                      data-testid={`coach-client-view-record-${c.member_id}`}
                    >
                      View Full Client Record
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {teamsQuery.isPending ? (
            <p className="text-sm text-ink-soft" data-testid="coach-teams-loading">Loading…</p>
          ) : teamsQuery.isError ? (
            <p className="text-sm text-red-600" data-testid="coach-teams-error">Couldn't load your teams.</p>
          ) : teams.length === 0 ? (
            <p className="text-sm text-ink-soft" data-testid="coach-teams-empty">No teams yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-4xl" data-testid="coach-teams-list">
              {teams.map((t) => (
                <div key={t.workspace_id} className="border border-ink/15 bg-white/50 rounded-lg p-4" data-testid={`coach-team-card-${t.workspace_id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-sm font-medium text-ink">{t.team_name}</p>
                      <p className="text-xs text-ink-soft">{t.programme_type} · {t.member_count} member{t.member_count === 1 ? "" : "s"}</p>
                    </div>
                    <span className="text-[10px] uppercase text-ink-soft border border-ink/10 rounded-full px-2 py-0.5 shrink-0">{t.status_bucket}</span>
                  </div>
                  <p className="text-xs text-ink-soft mb-1" data-testid={`coach-team-members-${t.workspace_id}`}>{t.members.join(", ")}</p>
                  <p className="text-xs text-ink-soft mb-1" data-testid={`coach-team-next-session-${t.workspace_id}`}>
                    {t.next_session_at ? `Next: ${new Date(t.next_session_at).toLocaleDateString("en-US", { timeZone: "America/Sao_Paulo", month: "short", day: "numeric" })}` : "No next session"}
                  </p>
                  {t.current_stage_label && (
                    <p className="text-xs text-ink-soft mb-1" data-testid={`coach-team-stage-${t.workspace_id}`}>Journey: {t.current_stage_label}</p>
                  )}
                  {t.open_task_count > 0 && (
                    <p className="text-xs text-terracotta font-medium mb-2" data-testid={`coach-team-open-tasks-${t.workspace_id}`}>{t.open_task_count} open task{t.open_task_count > 1 ? "s" : ""}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => navigate(`/rehearsal-room/coach/team/${t.workspace_id}/overview`)}
                      className="text-sm font-medium border border-ink/15 px-3 py-1.5 rounded-full text-ink hover:bg-ink/5 transition-colors"
                      data-testid={`coach-team-view-record-${t.workspace_id}`}
                    >
                      View Full Team Record
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
